import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, BedDouble, ChevronDown, Flame, HeartHandshake, MessageCircle, Sparkles, Tent, Train, Users, UtensilsCrossed, Waves } from "lucide-react";
import { EASE, Reveal, SectionHeading, WhatsAppButton, useWhatsApp } from "../components/Shared";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";
import { LangContext } from "../i18n";
import { ACCOMMODATION, AMENITIES, EVENTS } from "../data/destinations";

export default function KumbhPage() {
  const { lang, copy } = useContext(LangContext);
  const k = copy.kumbh;
  const [faq, setFaq] = useState(null);
  const [preset, setPreset] = useState({ cat: "", n: 0 });
  const openWhatsApp = useWhatsApp();

  const scrollTo = (sel) => () => {
    if (window.__lenis) window.__lenis.scrollTo(sel, { offset: -72 });
    else document.querySelector(sel)?.scrollIntoView({ behavior: "smooth" });
  };
  const enquireCategory = (cat) => {
    setPreset((p) => ({ cat, n: p.n + 1 }));
    scrollTo("#enquiry")();
  };

  return (
    <main className="kumbh-page">
      <Seo title={k.seoTitle} description={k.seoDesc} path="/kumbh" />

      <section className="kumbh-hero" data-testid="kumbh-hero">
        <img src="/assets/yagya-shala.jpg" alt={lang === "hi" ? "महाकुंभ में रात्रि की यज्ञ शाला" : "The akhada yagya shala pavilion lit at night during Kumbh"} className="kumbh-hero-bg" />
        <div className="kumbh-hero-overlay" />
        <div className="kumbh-hero-inner">
          <motion.p className="eyebrow" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: EASE }}>{k.heroKicker}</motion.p>
          <h1 key={lang}>
            <span className="mask-line"><motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.25, ease: EASE }}>{k.heroTitle}</motion.span></span>
          </h1>
          <motion.p className="kumbh-hero-sub" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6, ease: EASE }}>{k.heroSub}</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8, ease: EASE }}>
            <button className="button" onClick={scrollTo("#accommodation")} data-testid="kumbh-explore-stay"><Tent size={16} /> {k.ctaStay}</button>
            <button className="button button-outline light" onClick={scrollTo("#enquiry")} data-testid="kumbh-plan-button"><Sparkles size={16} /> {k.ctaEnquire}</button>
          </motion.div>
        </div>
      </section>

      <section className="section-pad sig-section">
        <div className="sig-split">
          <div>
            <SectionHeading data={{ kicker: k.sigKicker, title: k.sigTitle }} />
            <Reveal delay={0.1}><p className="body-copy">{k.sigBody}</p></Reveal>
          </div>
          <Reveal className="sig-visual" delay={0.15}>
            <img src="/assets/ganga-snaan.png" alt={lang === "hi" ? "संगम पर पवित्र स्नान करती श्रद्धालु" : "A devotee taking the holy dip at the Sangam"} loading="lazy" data-testid="kumbh-snan-image" />
          </Reveal>
        </div>
      </section>

      <section className="section-pad kumbh-dates">
        <SectionHeading data={{ kicker: k.datesKicker, title: k.datesTitle, sub: k.datesNote }} />
        <div className="event-row">
          {EVENTS.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 0.08} className="event-card-wrap">
              <Link to={`/kumbh/${ev.city}`} className="event-card" data-testid={`kumbh-event-${ev.id}`}>
                <span className={`status-badge ${ev.status}`}>{ev.status === "confirmed" ? (lang === "hi" ? "तिथियाँ घोषित" : "Dates confirmed") : ev.status === "tentative" ? (lang === "hi" ? "अपेक्षित" : "Expected") : (lang === "hi" ? "वार्षिक" : "Annual")}</span>
                <p className="event-city">{ev[lang].city}</p>
                <h3>{ev[lang].title}</h3>
                <p className="event-dates">{ev[lang].dates}</p>
                <p className="event-note">{ev[lang].note}</p>
                <span className="event-link">{copy.events.link} <ArrowUpRight size={14} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="accommodation" className="section-pad acc-section">
        <SectionHeading data={{ kicker: k.accKicker, title: k.accTitle, sub: k.accSub }} />
        <div className="acc-grid">
          {ACCOMMODATION.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.09} className="acc-card-wrap">
              <article className="acc-card" data-testid={`acc-card-${a.id}`}>
                <div className="acc-photo"><img src={a.img} alt={a[lang].alt} loading="lazy" /></div>
                <div className="acc-body">
                  <h3>{a[lang].name}</h3>
                  <p className="acc-spec"><BedDouble size={15} /> {a[lang].beds}</p>
                  <p className="acc-spec"><Users size={15} /> {k.sleeps}: {a[lang].sleeps}</p>
                  <div className="acc-amenities">
                    <span className="acc-amenities-title">{k.facilities}</span>
                    <ul>{AMENITIES[lang].map((am) => <li key={am}>{am}</li>)}</ul>
                  </div>
                  <div className="acc-price">
                    <span className="acc-price-value">{k.onRequest}</span>
                    <span className="acc-price-unit">{k.perTent}</span>
                  </div>
                  <button className="button acc-enquire" onClick={() => enquireCategory(a[lang].name)} data-testid={`acc-enquire-${a.id}`}>
                    {k.enquireNow} <ArrowUpRight size={15} />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="dark-section section-pad">
        <SectionHeading data={{ kicker: k.svcsKicker, title: k.svcsTitle }} dark />
        <div className="vedic-grid three">
          {k.svcs.map(([title, body], i) => {
            const Icon = [Waves, UtensilsCrossed, Train, Flame, HeartHandshake, Users][i];
            return (
              <Reveal key={title} delay={(i % 3) * 0.07} className="vedic-card-wrap">
                <article className="vedic-card" data-testid={`kumbh-service-${i}`}>
                  <span className="vedic-icon"><Icon size={20} /></span>
                  <h3>{title}</h3><p>{body}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="faq-section section-pad">
        <SectionHeading data={{ kicker: k.faqKicker, title: k.faqTitle }} />
        <div className="faq-list">
          {k.faqs.map(([q, a], i) => (
            <Reveal key={q} delay={i * 0.05}>
              <div className={`faq-item ${faq === i ? "open" : ""}`}>
                <button onClick={() => setFaq(faq === i ? null : i)} data-testid={`kumbh-faq-${i}`}>
                  <span>{q}</span>
                  <motion.span animate={{ rotate: faq === i ? 180 : 0 }} transition={{ duration: 0.3 }}><ChevronDown size={18} /></motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {faq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: EASE }} style={{ overflow: "hidden" }}>
                      <p data-testid={`kumbh-faq-answer-${i}`}>{a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="enquiry" className="section-pad enquiry-section">
        <div className="enquiry-wrap">
          <div className="enquiry-copy">
            <SectionHeading data={{ kicker: copy.enquiry.kicker, title: copy.enquiry.title, sub: copy.enquiry.sub }} />
            <p className="enquiry-side-note">{k.contactKicker}</p>
            <WhatsAppButton testid="kumbh-enquiry-whatsapp" onClick={() => openWhatsApp("kumbh")}>{copy.hero.primary}</WhatsAppButton>
          </div>
          <Reveal delay={0.1}>
            <EnquiryForm key={`${preset.cat}-${preset.n}-${lang}`} defaultCategory={preset.cat} />
          </Reveal>
        </div>
      </section>

      <section className="kumbh-final section-pad">
        <Reveal>
          <p className="eyebrow">{k.contactKicker}</p>
          <h2>{k.contactTitle}</h2>
          <div className="hero-actions">
            <WhatsAppButton testid="kumbh-final-whatsapp" onClick={() => openWhatsApp("kumbh")}><MessageCircle size={17} /> WhatsApp</WhatsAppButton>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
